import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ConfigProvider, Layout, theme, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { verifyToken, loginUser } from './store/slices/authSlice';
import useTelegram from './hooks/useTelegram';

// Layout
import Header from './components/layout/Header';
import MobileBottomNav from './components/layout/MobileBottomNav';
import GlobalBackButton from './components/layout/GlobalBackButton';

// Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailsPage from './pages/ProductDetailsPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import ProfilePage from './pages/ProfilePage';
import OrderDetailPage from './pages/OrderDetailPage';
import AdminPage from './pages/AdminPage';
import SellerPage from './pages/SellerPage';
import AuthPage from './pages/AuthPage';

// Admin Components
import Dashboard from './components/admin/Dashboard';
import ProductManager from './components/admin/ProductManager';
import CategoryManager from './components/admin/CategoryManager';
import SellerManager from './components/admin/SellerManager';
import RegionManager from './components/admin/RegionManager';
import Statistics from './components/admin/Statistics';
import AdminOrderManager from './components/admin/AdminOrderManager';

// Seller Components
import SellerDashboard from './components/seller/SellerDashboard';
import PendingOrders from './components/seller/PendingOrders';
import OrderManager from './components/seller/OrderManager';

const { Content } = Layout;

function App() {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.auth);
  const { user: tgUser, initData } = useTelegram();

  // We use light algorithm for standard components inside the dark background
  const activeAlgorithm = theme.defaultAlgorithm;

  useEffect(() => {
    // Auto-login/register with Telegram
    const token = localStorage.getItem('token');

    if (initData && tgUser) {
      // If opened inside Telegram, always use initData to authenticate 
      // This ensures we always recognize the active Telegram user
      dispatch(loginUser({ initData }))
        .unwrap()
        .catch((err) => {
          console.error('Telegram auto-login failed:', err);
          // Fallback to token if Telegram login fails
          if (token) {
            dispatch(verifyToken());
          }
        });
    } else if (token) {
      // If not in Telegram (e.g., browser) but has token, verify it
      dispatch(verifyToken());
    }
  }, [dispatch, tgUser, initData]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="Yuklanmoqda..." />
      </div>
    );
  }

  return (
    <ConfigProvider
      theme={{
        algorithm: activeAlgorithm,
        token: {
          colorPrimary: '#0ea5e9', // Brighter cyan/blue
          borderRadius: 12,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
          colorBgContainer: '#ffffff', // White cards
          colorBgElevated: '#ffffff',
          colorBorderSecondary: 'rgba(0,0,0,0.05)',
        },
        components: {
          Card: {
            boxShadowTertiary: '0 4px 20px rgba(0, 0, 0, 0.05)',
          }
        }
      }}
    >
      <Router>
        <Routes>
          {/* Auth Pages - outside main layout */}
          <Route path="/login" element={<AuthPage />} />
          <Route path="/register" element={<AuthPage />} />

          {/* Main Layout Routes */}
          <Route path="/*" element={
            <Layout style={{ minHeight: '100vh', background: 'transparent' }}>
              <Header />
              <div style={{ paddingTop: '16px' }}>
                <GlobalBackButton />
              </div>
              <Content className="main-layout-content" style={{ padding: '0 16px 16px 16px', background: 'transparent', transition: 'all 0.3s' }}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/products" element={<ProductsPage />} />
                  <Route path="/products/:id" element={<ProductDetailsPage />} />
                  <Route path="/cart" element={<CartPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/orders" element={<OrdersPage />} />
                  <Route path="/orders/:id" element={<OrderDetailPage />} />
                  <Route path="/profile" element={<ProfilePage />} />

                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminPage />}>
                    <Route index element={<Dashboard />} />
                    <Route path="products" element={<ProductManager />} />
                    <Route path="categories" element={<CategoryManager />} />
                    <Route path="sellers" element={<SellerManager />} />
                    <Route path="regions" element={<RegionManager />} />
                    <Route path="orders" element={<AdminOrderManager />} />
                    <Route path="statistics" element={<Statistics />} />
                  </Route>

                  {/* Seller Routes */}
                  <Route path="/seller" element={<SellerPage />}>
                    <Route index element={<SellerDashboard />} />
                    <Route path="pending-orders" element={<PendingOrders />} />
                    <Route path="orders/:id" element={<OrderManager />} />
                  </Route>
                </Routes>
              </Content>
              <MobileBottomNav />
            </Layout>
          } />
        </Routes>
      </Router>
    </ConfigProvider>
  );
}

export default App;
